<template>
	<ext-dialog
		v-model="value"
		:title="dialogTitle"
		:primaryButtonText="$t('save')"
		@primaryButtonAction="save"
		@closed="close"
	>
		<template #body>
			<v-form ref="form" v-model="valid">
				<v-text-field v-model="stock.lotNo" :label="$t('lot_no')" />

				<product-select
					:key="stock.variantInfo?.variantId || 'new'"
					v-model="stock.variantInfo"
					:label="$t('product')"
					:rules="rules.variant"
					:eager="!editing"
					@change="handleVariantChange"
					:disabled="editing"
					required
				/>

				<format-select
					class="mb-4"
					v-model="stock.formatKey"
					:items="formatOptions"
					:rules="rules.format"
					:label="$t('format')"
					:disabled="!stock.variantId"
					:menu-props="{ offsetY: true }"
					@change="onFormatChange"
					required
				/>

				<div v-for="attr in sorted.multi_select" :key="attr.id" class="mb-2 pb-2">
					<v-select
						v-model="attributeValues[attr.id]"
						:items="
							attr.options
								.filter((o) => !attr.allowed || attr.allowed.includes(o._id))
								.map((o) => ({ text: o.value[$lang] || o.value._default, value: o._id }))
						"
						:label="attr.name[$lang] || attr.name._default"
						chips
						small-chips
						deletable-chips
						outlined
						hide-details
						append-icon="icon-angle-down"
						:menu-props="{ offsetY: true }"
						multiple
					/>
				</div>

				<div v-for="attr in sorted.others" :key="attr.id" class="mb-2 pb-2">
					<v-menu
						v-if="attr.type === 'date'"
						v-model="datePickers[attr.id]"
						:close-on-content-click="false"
						offset-y
						min-width="290"
					>
						<template #activator="{ on, attrs }">
							<v-text-field
								v-bind="attrs"
								v-on="on"
								v-model="attributeValues[attr.id]"
								:label="attr.name[$lang] || attr.name._default"
								readonly
								hide-details
							/>
						</template>
						<v-date-picker v-model="attributeValues[attr.id]" @input="datePickers[attr.id] = false" />
					</v-menu>
					<v-text-field
						v-else
						v-model="attributeValues[attr.id]"
						:label="attr.name[$lang] || attr.name._default"
						outlined
						hide-details
					/>
				</div>

				<v-row v-if="sorted.boolean.length" class="mb-2 pb-2">
					<v-col v-for="attr in sorted.boolean" :key="attr.id" cols="12" sm="4">
						<v-switch v-model="attributeValues[attr.id]" :label="attr.name[$lang] || attr.name._default" hide-details />
					</v-col>
				</v-row>

				<v-select
					class="mt-4"
					v-model="stock.warehouse"
					:items="warehouseOptions"
					item-value="_id"
					item-text="name"
					:rules="rules.warehouse"
					:label="$t('warehouse')"
					outlined
					required
					append-icon="icon-angle-down"
					:menu-props="{ offsetY: true }"
				/>

				<v-text-field v-model="stock.notes" :label="$t('notes')" />
			</v-form>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import ProductSelect from "../../../controls/ProductSelect.vue";
import FormatSelect from "../../../controls/FormatSelect.vue";
import remote from "../../../../data/remote";
import { mapGetters } from "vuex";

export default {
	name: "NewStockDialog",
	components: { ExtDialog, ProductSelect, FormatSelect },
	model: { prop: "value", event: "input" },
	props: { value: Boolean, stockData: Object, companyId: String },
	data() {
		return {
			valid: false,
			stock: this.emptyStock(),
			warehouseOptions: [],
			formatOptions: [],
			attributeOptions: [],
			attributeValues: {},
			datePickers: {},
			showErrors: false,
			rules: {
				variant: [
					(v) => (this.showErrors ? Boolean(v) : true) || this.$t("$validation.required", { item: this.$t("variant") }),
				],
				format: [(v) => (this.showErrors ? !!v : true) || this.$t("$validation.required", { item: this.$t("format") })],
				warehouse: [
					(v) =>
						(this.showErrors ? Boolean(v) : true) || this.$t("$validation.required", { item: this.$t("warehouse") }),
				],
			},
		};
	},
	computed: {
		...mapGetters(["company"]),
		$lang() {
			return this.$i18n.locale;
		},
		dialogTitle() {
			return this.editing ? this.$t("edit_stock") : this.$t("add_new_stock");
		},
		editing() {
			return !!this.stockData;
		},
		sorted() {
			const multi = [],
				bool = [],
				other = [];
			this.attributeOptions.forEach((a) => {
				if (a.type === "multi_select") multi.push(a);
				else if (a.type === "boolean") bool.push(a);
				else other.push(a);
			});
			return { multi_select: multi, boolean: bool, others: other };
		},
	},
	watch: {
		value(open) {
			if (open) {
				this.loadWarehouses();
				this.initData();
			}
		},
	},
	methods: {
		emptyStock() {
			return {
				productId: "",
				variantId: "",
				formatKey: null,
				variantInfo: null,
				warehouse: "",
				unit: "",
				lotNo: "",
				notes: "",
				quantities: [],
			};
		},
		close() {
			this.$emit("input", false);
			this.reset();
		},
		reset() {
			this.stock = this.emptyStock();
			this.formatOptions = [];
			this.attributeOptions = [];
			this.attributeValues = {};
			this.showErrors = false;
			this.$refs.form?.reset();
		},
		async initData() {
			this.reset();
			if (!this.editing) return;
			const s = this.stockData;
			this.stock.id = s.id || s._id;
			this.stock.productId = s.productId?.id || s.productId?._id || s.productId;
			this.stock.variantId = s.variantId;
			this.stock.lotNo = s.lotNo;
			this.stock.notes = s.notes;
			this.stock.warehouse = s.warehouse;
			this.stock.unit = s.unit;
			this.stock.variantInfo = { productId: this.stock.productId, variantId: s.variantId, label: s.productName };
			this.attributeValues = {};
			const raw = Array.isArray(s.attributeObjs) ? s.attributeObjs : s.attributes;
			if (Array.isArray(raw)) raw.forEach((av) => this.$set(this.attributeValues, av.attributeId.toString(), av.value));
			const meta = await new Promise((res, rej) =>
				remote.products.variantMeta({
					productId: this.stock.productId,
					variantId: this.stock.variantId,
					lang: this.$lang,
					onSuccess: res,
					onFail: rej,
				})
			);
			this.buildFromMeta(meta, true);
			this.stock.quantities = s.quantities || [];
		},
		buildFromMeta(meta, forEditing) {
			this.formatOptions = meta.formatOptions || [];
			if (forEditing) {
				const mid = this.stockData.measurementId?.toString() || this.stockData.measurementId;
				const found = this.formatOptions.find((o) => String(o.measurementId) === String(mid));
				if (found) {
					this.stock.formatKey = found.key;
					this.stock.unit = found.unit || "";
				}
			} else if (this.formatOptions.length === 1) {
				this.stock.formatKey = this.formatOptions[0].key;
				this.stock.unit = this.formatOptions[0].unit || "";
			}
			const allowedMap = {};
			(meta.variantAttributes || []).forEach((a) => {
				const id = a.attributeId.toString();
				allowedMap[id] = Array.isArray(a.value) ? a.value : a.value != null ? [a.value] : [];
			});
			this.attributeOptions = (meta.attributeOptions || [])
				.map((a) => {
					const id = a._id.toString();
					if (!allowedMap[id]) return null;
					return a.type === "multi_select" ? { ...a, id, allowed: allowedMap[id] } : { ...a, id };
				})
				.filter(Boolean);
		},
		handleVariantChange(o) {
			if (!o || this.editing) return;
			this.stock.productId = o.productId;
			this.stock.variantId = o.variantId;
			this.stock.formatKey = null;
			this.stock.unit = "";
			remote.products.variantMeta({
				productId: o.productId,
				variantId: o.variantId,
				lang: this.$lang,
				onSuccess: (meta) => this.buildFromMeta(meta, false),
				onFail: () => {},
			});
		},
		onFormatChange(key) {
			const sel = this.formatOptions.find((o) => o.key === key);
			if (sel) this.stock.unit = sel.unit || "";
		},
		save() {
			this.showErrors = true;
			if (!this.$refs.form.validate()) return;
			const sel = this.formatOptions.find((o) => o.key === this.stock.formatKey);
			if (!sel) {
				this.$toast?.error(this.$t("please_select_format"));
				return;
			}
			const attrsArr = Object.entries(this.attributeValues).map(([id, val]) => ({ attributeId: id, value: val }));
			const payload = {
				product: {
					productId: this.stock.productId,
					variantId: this.stock.variantId,
					formatId: sel.formatId,
					measurementId: sel.measurementId,
					unit: sel.unit || "",
					notes: this.stock.notes,
					attributes: attrsArr,
				},
				warehouse: this.stock.warehouse,
				lotNo: this.stock.lotNo,
			};
			if (this.editing) {
				payload.id = this.stock.id;
				remote.stocks.update(payload, {
					onSuccess: (r) => {
						this.$emit("saved", r);
						this.close();
					},
				});
			} else {
				remote.stocks.create(payload, {
					onSuccess: (r) => {
						this.$emit("saved", r);
						this.close();
					},
				});
			}
		},
		loadWarehouses() {
			const id = this.companyId || this.company?.id;
			if (id) remote.warehouses.filter(id, { onSuccess: (r) => (this.warehouseOptions = r) });
		},
	},
};
</script>
